import { FC, useMemo, useState } from 'react';
import LaunchIcon from '@mui/icons-material/Launch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import { MutualFundMark, MutualFundMarksResponse } from 'services/hooks/companies';
import {
  AvgPrice,
  CardHeaderRow,
  ChangeLabel,
  FundFilterRow,
  FundPill,
  HeaderLeft,
  MarksTable,
  MarksTableCell,
  MarksTableHead,
  MarksTableRow,
  SeriesChip,
  SeriesToggleRow,
  ViewToggle,
} from './MutualFundMarksCard.styles';
import {
  buildChartSeries,
  buildFundOverlaySeries,
  computeHeaderStats,
  EndDateMode,
  EndDateModeValue,
  formatChartDate,
  formatCurrency,
  formatIsoDate,
  formatXAxisLabel,
  getFundLabel,
  getLastQuarterWeightedAvg,
  getPeriodStartDate,
  getPricePerShare,
  Period,
  PERIODS,
  PeriodValue,
  toNum,
  ViewMode,
  ViewModeValue,
} from './MutualFundMarksCard.utils';

interface Props {
  data: MutualFundMarksResponse | undefined;
  isLoading: boolean;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
  view: ViewModeValue;
  onViewChange: (v: ViewModeValue) => void;
  period: PeriodValue;
  onPeriodChange: (v: PeriodValue) => void;
}

const EdgarMarksBody: FC<Props> = ({
  data,
  isLoading,
  measurementDate,
  endDateMode,
  view,
  onViewChange,
  period,
  onPeriodChange,
}) => {
  const uniqueFundLabels = data?.fund_families ?? [];
  const [enabledSeriesIds, setEnabledSeriesIds] = useState(new Set(['avg', 'max', 'min']));
  const [selectedFunds, setSelectedFunds] = useState<Set<string>>(new Set());

  const latestDataDate = useMemo(() => {
    if (!data?.marks.length) {
      return undefined;
    }
    return [...data.marks].sort((a, b) => b.reporting_date.localeCompare(a.reporting_date))[0].reporting_date;
  }, [data]);

  const effectiveEndDate = useMemo(() => endDateMode === EndDateMode.Measurement && measurementDate
    ? measurementDate
    : latestDataDate, [endDateMode, measurementDate, latestDataDate]);

  const filteredMarks = useMemo(() => {
    if (!data) {
      return [];
    }
    let { marks } = data;

    if (effectiveEndDate) {
      marks = marks.filter(m => m.reporting_date <= effectiveEndDate);
    }

    if (period !== Period.All && effectiveEndDate) {
      const startDate = getPeriodStartDate(effectiveEndDate, period);
      if (startDate) {
        marks = marks.filter(m => m.reporting_date >= startDate);
      }
    }

    return marks;
  }, [data, period, effectiveEndDate]);

  const { value: lastWeightedAvg, quarter: lastWeightedAvgQuarter } = useMemo(
    () => getLastQuarterWeightedAvg(data?.marks ?? [], effectiveEndDate),
    [data?.marks, effectiveEndDate],
  );

  const { series: rawSeries, singleFund } = useMemo(() => buildChartSeries(filteredMarks), [filteredMarks]);
  const series = useMemo(
    () => singleFund ? rawSeries : rawSeries.filter(s => enabledSeriesIds.has(s.id)),
    [rawSeries, singleFund, enabledSeriesIds],
  );
  const overlaySeries = useMemo(
    () => singleFund ? [] : buildFundOverlaySeries(filteredMarks, [...selectedFunds]),
    [filteredMarks, singleFund, selectedFunds],
  );
  const tableMarks = useMemo(
    () => selectedFunds.size === 0 ? filteredMarks : filteredMarks.filter(m => selectedFunds.has(getFundLabel(m))),
    [filteredMarks, selectedFunds],
  );
  const { change } = useMemo(() => computeHeaderStats(filteredMarks), [filteredMarks]);

  const hasData = (data?.marks.length ?? 0) > 0;

  return (
    <>
      <CardHeaderRow>
        <HeaderLeft>
          {hasData && lastWeightedAvg !== null && (
            <>
              <AvgPrice>{formatCurrency(lastWeightedAvg)}</AvgPrice>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                weighted avg / share{lastWeightedAvgQuarter ? ` · ${formatIsoDate(lastWeightedAvgQuarter)}` : ''}
              </Typography>
              {change && (
                <ChangeLabel positive={change.abs >= 0}>
                  {change.abs >= 0 ? '+' : ''}{change.abs.toFixed(2)} ({change.abs >= 0 ? '+' : ''}{change.pct.toFixed(2)}%)
                </ChangeLabel>
              )}
              {latestDataDate && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                  Data through {formatIsoDate(effectiveEndDate && effectiveEndDate < latestDataDate ? effectiveEndDate : latestDataDate)}
                </Typography>
              )}
            </>
          )}
        </HeaderLeft>
        <ViewToggle>
          {hasData && (
            <ToggleButtonGroup
              value={period}
              exclusive
              size="small"
              onChange={(_e, v: PeriodValue | null) => {
                if (v) {
                  onPeriodChange(v);
                }
              }}
            >
              {PERIODS.map(p => (
                <ToggleButton key={p} value={p}>{p}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_e, v: ViewModeValue | null) => {
              if (v) {
                onViewChange(v);
              }
            }}
          >
            <ToggleButton value={ViewMode.Chart}>Chart</ToggleButton>
            <ToggleButton value={ViewMode.Table}>Table</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
            href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&type=NPORT-P&dateb=&owner=include&count=40"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', borderColor: 'warning.main', color: 'warning.dark', height: 26, py: 0 }}
          >
            SEC Filing
          </Button>
        </ViewToggle>
      </CardHeaderRow>

      {isLoading && (
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
      )}

      {!isLoading && !hasData && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
          No mutual fund marks available for this company.
        </Typography>
      )}

      {!isLoading && hasData && (
        <>
          {!singleFund && rawSeries.length > 0 && (
            <SeriesToggleRow>
              {rawSeries.map(s => (
                <SeriesChip
                  key={s.id}
                  label={s.label}
                  size="small"
                  variant="outlined"
                  active={enabledSeriesIds.has(s.id)}
                  seriesColor={s.color ?? '#1976d2'}
                  onClick={() => {
                    const next = new Set(enabledSeriesIds);
                    if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                    setEnabledSeriesIds(next);
                  }}
                />
              ))}
            </SeriesToggleRow>
          )}
          {!singleFund && uniqueFundLabels.length > 1 && (
            <FundFilterRow>
              {uniqueFundLabels.map((label: string) => (
                <FundPill
                  key={label}
                  label={label}
                  size="small"
                  variant="outlined"
                  active={selectedFunds.has(label)}
                  onClick={() => {
                    const next = new Set(selectedFunds);
                    if (next.has(label)) next.delete(label); else next.add(label);
                    setSelectedFunds(next);
                  }}
                />
              ))}
            </FundFilterRow>
          )}

          {view === ViewMode.Chart && (
            <ScalarLineChart
              series={[...series, ...overlaySeries]}
              notMerge
              smooth
              showPoints
              style={{ height: 280 }}
              config={{
                legend: { show: true, position: 'top' },
                xAxis: { formatter: v => formatXAxisLabel(v, singleFund) },
                yAxis: { formatter: v => `$${v}` },
                options: {
                  tooltip: {
                    trigger: 'axis',
                    appendToBody: true,
                    formatter: (params: any) => {
                      if (!Array.isArray(params) || !params.length) return '';
                      const dateLabel = formatChartDate(String(params[0].axisValue));
                      return (
                        `<div style="font-weight:700;margin-bottom:4px;font-size:0.82rem">${dateLabel}</div>${params
                          .filter((p: any) => p.value != null)
                          .map((p: any) => `<div>${p.marker}${p.seriesName}: <b>$${Number(p.value).toFixed(2)}</b></div>`)
                          .join('')}`
                      );
                    },
                  },
                },
              }}
            />
          )}

          {view === ViewMode.Table && (
            <MarksTable>
              <thead>
                <MarksTableRow>
                  <MarksTableHead>Fund</MarksTableHead>
                  <MarksTableHead>Title</MarksTableHead>
                  <MarksTableHead>Date</MarksTableHead>
                  <MarksTableHead align="right">Price / Share</MarksTableHead>
                  <MarksTableHead align="right">Fair Value</MarksTableHead>
                  <MarksTableHead align="right">Shares Held</MarksTableHead>
                  <MarksTableHead align="center">Filing</MarksTableHead>
                </MarksTableRow>
              </thead>
              <tbody>
                {[...tableMarks].reverse().map((m: MutualFundMark, i: number) => (
                  <MarksTableRow key={i}>
                    <MarksTableCell>{getFundLabel(m)}</MarksTableCell>
                    <MarksTableCell>{m.title ?? '—'}</MarksTableCell>
                    <MarksTableCell>{formatIsoDate(m.reporting_date)}</MarksTableCell>
                    <MarksTableCell align="right">{formatCurrency(getPricePerShare(m))}</MarksTableCell>
                    <MarksTableCell align="right">{formatCurrency(toNum(m.fair_value_usd))}</MarksTableCell>
                    <MarksTableCell align="right">{m.shares_held?.toLocaleString() ?? '—'}</MarksTableCell>
                    <MarksTableCell align="center">
                      <IconButton
                        size="small"
                        component="a"
                        href={m.filing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main' }}
                      >
                        <LaunchIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </MarksTableCell>
                  </MarksTableRow>
                ))}
              </tbody>
            </MarksTable>
          )}
        </>
      )}
    </>
  );
};

export { EdgarMarksBody };
